import type React from "react";
import { memo, useEffect, useRef, useState, type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Pill } from "@/components/podkit/status/Pill";
import { Button } from "@/components/flexkit/Button";
import {
    useIsUserAuthenticatedWithRunnerForHost,
    useRunner,
    useSearchRepositoriesQuery,
} from "@/queries/runner-queries";
import { CornerDownLeft } from "lucide-react";
import { withGitpodSize } from "@/assets/icons/utils";
import { SearchMode, type SearchRepositoriesResponse_Repository } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import IconTasks from "central-icons/IconTasks";
import IconCrossSmall from "central-icons/IconCrossSmall";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { getScmDisplayName } from "@/components/automations/scm-utils";
import { RepoTriggerSampleDialog } from "@/components/automations/RepoTriggerSampleDialog";
import { Input } from "@/components/podkit/forms/Input";
import { Banner } from "@/components/flexkit/Banner";
import { AuthenticationModal } from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { TrackLocations } from "@/hooks/use-segment";

export const RepoFilteringContent: FC<{
    repoSearchString: string;
    runnerId: string;
    disabled: boolean;
    scmHost: string;
    onScmHostChange: (scmHost: string) => void;
    onAddFilter: (filter: string) => void;
    onClearFilter: () => void;
}> = memo(
    ({
        disabled,
        repoSearchString,
        runnerId,
        scmHost: selectedScmHost,
        onAddFilter,
        onClearFilter,
        onScmHostChange,
    }) => {
        const [showAuthDialog, setShowAuthDialog] = useState(false);

        const {
            data: scmIntegrations,
            isLoading: isLoadingSCMIntegrations,
            error: listSCMIntegrationsError,
            refetch: refetchSCMIntegrations,
        } = useListRunnerSCMIntegrations(runnerId);

        const { data: runner } = useRunner(runnerId);

        let scmHost: string;
        let scmId: string;
        if (scmIntegrations && scmIntegrations.length > 0) {
            // find the SCM integration that matches the selected SCM host
            const selected = scmIntegrations.find((it) => it.host === selectedScmHost);
            if (selected) {
                scmHost = selected.host;
                scmId = selected.scmId;
            } else {
                // if the selected SCM host is not found in the list of SCM integrations, use the first integration
                scmHost = scmIntegrations[0].host;
                scmId = scmIntegrations[0].scmId;
            }
        } else {
            scmHost = "";
            scmId = "";
        }

        const shouldCheckAuth = !!runnerId && !!scmHost;
        const {
            data: authData,
            isPending: isPendingAuthData,
            error: authDataError,
        } = useIsUserAuthenticatedWithRunnerForHost(runnerId, scmHost, {
            enabled: shouldCheckAuth,
            retryOnFailedPrecondition: true,
            refetchUntilAuthenticated: true,
        });
        const missingAuthentication =
            shouldCheckAuth && !isPendingAuthData && authData?.type === "AuthenticationRequired";

        const {
            data: repositories,
            isLoading: isSearching,
            error: searchRepositoriesError,
            refetch: refetchRepositories,
        } = useSearchRepositoriesQuery({
            runnerId,
            query: repoSearchString,
            limit: 25,
            scmHost,
            enabled: !disabled && scmHost !== "" && repoSearchString !== "",
            searchMode: SearchMode.NATIVE,
        });

        useEffect(() => {
            if (scmIntegrations && scmIntegrations.length > 0 && !selectedScmHost) {
                // if the selected SCM host is not set, use the first scm integration found for the runner
                onScmHostChange(scmIntegrations[0].host);
            }
        }, [scmIntegrations, onScmHostChange, selectedScmHost]);

        if (isLoadingSCMIntegrations) {
            return (
                <Text className="animate-pulse text-base text-content-secondary">
                    Finding suitable repository provider…
                </Text>
            );
        }

        if (listSCMIntegrationsError) {
            return (
                <div className="flex flex-row items-baseline gap-2">
                    <ErrorMessage error={listSCMIntegrationsError} />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void refetchSCMIntegrations();
                        }}
                        data-tracking-id="retry"
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        if (shouldCheckAuth && isPendingAuthData) {
            return (
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Text className="text-content-muted">Checking authentication</Text>
                </div>
            );
        }

        if (isSearching) {
            return <Text className="animate-pulse text-base text-content-secondary">Searching repositories…</Text>;
        }

        if (repositories) {
            return (
                <RepoFilterResult searchString={repoSearchString} repositories={repositories} onClear={onClearFilter} />
            );
        }

        if (scmIntegrations && scmIntegrations.length === 0) {
            return (
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Text className="text-content-muted">
                        {runner?.name} does not support repository trigger. Please select a different runner.
                    </Text>
                </div>
            );
        }

        if (missingAuthentication) {
            return (
                <>
                    <ErrorMessage error={authDataError} />
                    <Banner
                        action={{
                            text: "Authenticate",
                            onClick: () => setShowAuthDialog(true),
                            "data-tracking-id": "automation-edit-code-context-scm-authentication",
                        }}
                        variant="warning"
                        text={
                            <span>
                                <span className="font-bold">{runner?.name || "Unknown runner"}</span> requires
                                authentication with <span className="font-bold">{scmHost}</span>
                            </span>
                        }
                        className="shadow-none"
                    />
                    {scmHost && runnerId && authData && showAuthDialog && (
                        <AuthenticationModal
                            repoURL={"https://" + scmHost}
                            authResponse={authData}
                            runnerId={runnerId}
                            onAuthSuccess={() => {
                                setShowAuthDialog(false);
                            }}
                            onClose={() => {
                                setShowAuthDialog(false);
                            }}
                            data-track-location={TrackLocations.GitAuthenticationTokenCreateModal}
                        />
                    )}
                </>
            );
        }

        return (
            <RepoFilterInput
                initialSearchString={repoSearchString}
                loading={isSearching}
                disabled={disabled}
                onAddFilter={onAddFilter}
                searchError={searchRepositoriesError}
                onRetry={refetchRepositories}
                scmId={scmId}
            />
        );
    },
);
RepoFilteringContent.displayName = "RepoFilteringContent";

const RepoFilterInput: FC<{
    initialSearchString: string;
    loading: boolean;
    disabled: boolean;
    onAddFilter: (filter: string) => void;
    searchError?: Error | null;
    onRetry?: () => void;
    scmId?: string;
}> = memo(({ initialSearchString, loading, disabled, onAddFilter, searchError, onRetry, scmId }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [showError, setShowError] = useState(!!searchError);

    useEffect(() => {
        setShowError(!!searchError);
    }, [searchError]);

    const submitSearchString = () => {
        if (inputRef.current) {
            const value = inputRef.current.value;
            if (searchError && value === initialSearchString && onRetry) {
                // Same search string and there was an error - retry the query
                onRetry();
            } else {
                onAddFilter(value);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            submitSearchString();
        }
    };

    const handleChange = () => {
        if (showError) {
            setShowError(false);
        }
    };

    const scmDisplayName = scmId ? getScmDisplayName(scmId) : "repository";
    const placeholderText =
        scmDisplayName === "repository"
            ? "Describe what repositories to include or paste a filter"
            : `Describe what repositories to include or paste a ${scmDisplayName} filter`;

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-2">
                <Input
                    ref={inputRef}
                    disabled={disabled}
                    defaultValue={initialSearchString}
                    type="text"
                    placeholder={placeholderText}
                    className="max-w-none"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="aspect-square p-0"
                    loading={loading}
                    disabled={disabled}
                    LeadingIcon={withGitpodSize(CornerDownLeft)}
                    onClick={submitSearchString}
                    data-tracking-id="submitsearchstring"
                >
                    <span className="sr-only">Add repository search filter</span>
                </Button>
            </div>
            {showError && searchError && <ErrorMessage error={searchError} />}
        </div>
    );
});
RepoFilterInput.displayName = "RepoFilterInput";

const RepoFilterResult: FC<{
    searchString: string;
    repositories: PlainMessage<SearchRepositoriesResponse_Repository>[];
    onClear: () => void;
}> = memo(({ searchString, repositories, onClear }) => {
    const [openRepoTriggerSampleDialog, setOpenRepoTriggerSampleDialog] = useState(false);

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between rounded-lg border border-border-base p-3">
                    <Pill variant="brand" size="md">
                        {searchString}
                    </Pill>
                    <Button
                        variant="ghost"
                        size="sm"
                        LeadingIcon={withGitpodSize(IconCrossSmall)}
                        onClick={onClear}
                        data-tracking-id="code-context-repo-search-clear"
                    >
                        Clear
                    </Button>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <Text className="flex-1 text-content-secondary">{repositories.length} matching repositories</Text>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        LeadingIcon={withGitpodSize(IconTasks)}
                        data-tracking-id="code-context-repo-search-view-sample"
                        onClick={() => {
                            setOpenRepoTriggerSampleDialog(true);
                        }}
                    >
                        View sample
                    </Button>
                </div>
            </div>
            <RepoTriggerSampleDialog
                repositories={repositories}
                open={openRepoTriggerSampleDialog}
                onOpenChange={setOpenRepoTriggerSampleDialog}
            />
        </>
    );
});
RepoFilterResult.displayName = "RepoFilterResult";
